'use client';
import { useLayoutEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { Numeric } from '@/components/ui/numeric';
import type { EventPlayer } from '../../libs/eventPlayers';
import { eventPlayerHref } from '../../libs/eventPlayers';
import type {
  JourneyFixture,
  JourneyOpponentPath,
  JourneySource,
  PlayerJourney,
} from '../../libs/playerJourney';
import { NAME_WRAP } from '../../libs/nameBreaks';
import { displayMatchCode } from '../../libs/matchCode';
import type { JourneyContext } from './JourneyFixtureCard';
import { NameText } from './NameText';

type Wires = { next: string; future: string; feeders: string };
/** Courtside's bracket: each round box sits centred on where its feeders join. `pad` pushes
 * the names down when the box is taller than they are; `box` drops the box to the junction. */
type Align = Record<'next' | 'future', { pad: number; box: number }>;
const NO_ALIGN: Align = { next: { pad: 0, box: 0 }, future: { pad: 0, box: 0 } };
const compactTime = (iso: string, locale: string, timeZone?: string) =>
  new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));

/** Two compact rungs, folded at every width. Text stays in flow; wires follow measured boxes. */
export function PlayerDrawPath({
  journey,
  player,
  opponents,
  division,
  ...context
}: JourneyContext & {
  journey: PlayerJourney;
  player: EventPlayer;
  opponents: EventPlayer[];
  division: string;
}) {
  const t = useTranslations('Event.playerJourney');
  const root = useRef<HTMLDivElement>(null);
  const [wires, setWires] = useState<Wires>({ next: '', future: '', feeders: '' });
  const [align, setAlign] = useState<Align>(NO_ALIGN);
  const next = journey.next;
  const future = journey.ifWin?.stageType === 'elimination' ? journey.ifWin : null;
  // Layout effect: measure and align before paint, so the names and boxes never jump.
  useLayoutEffect(() => {
    const container = root.current;
    if (!container) return;
    const measure = () => {
      const base = container.getBoundingClientRect();
      const rect = (el: Element) => {
        const b = el.getBoundingClientRect();
        return { x: b.left - base.left, y: b.top - base.top, w: b.width, h: b.height };
      };
      const box = (name: string) => {
        const el = container.querySelector(`[data-journey-node="${name}"]`);
        return el ? rect(el) : null;
      };
      const own = box('own'),
        n = box('next'),
        f = box('future');
      if (!own || !n) return;
      const cx = (b: NonNullable<typeof n>) => b.x + b.w / 2;
      const cy = (b: NonNullable<typeof n>) => b.y + b.h / 2;
      const nextAlign: Align = structuredClone(NO_ALIGN);
      // Courtside's drawPathLayout: a pair's leaves meet at a join column, two groups'
      // junctions meet at a merge column, and one line runs to the box at their midpoint.
      // A lone name has nothing to join, so it runs straight to the box.
      const feeder = (prefix: 'next' | 'future', target: NonNullable<typeof n>) => {
        const column = container.querySelector(`[data-journey-feeders="${prefix}"]`);
        const groups = [...container.querySelectorAll(`[data-journey-group^="${prefix}-"]`)]
          .map((group) => [...group.querySelectorAll('[data-journey-leaf]')].map(rect))
          .filter((leaves) => leaves.length);
        if (!column || !groups.length) return '';
        const leafEnd = groups[0][0].x + groups[0][0].w;
        const gap = target.x - leafEnd;
        const joinX = leafEnd + gap * 0.3;
        const mergeX = leafEnd + gap * 0.6;
        const junctions = groups.map(
          (leaves) => (cy(leaves[0]) + cy(leaves[leaves.length - 1])) / 2,
        );
        const mid = (junctions[0] + junctions[junctions.length - 1]) / 2;
        // Centre the box on the junction midpoint, measured from where the names are laid out
        // (not column top + the pad we asked for: the browser snaps padding to its pixel grid,
        // and chasing that difference never settles).
        const below = mid - rect(column.firstElementChild ?? column).y;
        const pad = Math.max(0, target.h / 2 - below);
        nextAlign[prefix] = { pad, box: pad > 0 ? 0 : below - target.h / 2 };
        if (groups.length === 1 && groups[0].length === 1) return `M${leafEnd} ${mid}H${target.x}`;
        const paths = groups.map((leaves) => {
          const first = cy(leaves[0]),
            last = cy(leaves[leaves.length - 1]);
          return leaves.length > 1
            ? `M${leafEnd} ${first}H${joinX}V${last}H${leafEnd}`
            : `M${leafEnd} ${first}H${joinX}`;
        });
        if (junctions.length > 1)
          paths.push(
            `M${joinX} ${junctions[0]}H${mergeX}V${junctions[junctions.length - 1]}H${joinX}`,
            `M${mergeX} ${mid}H${target.x}`,
          );
        else paths.push(`M${joinX} ${junctions[0]}H${target.x}`);
        return paths.join(' ');
      };
      const measured = {
        next: `M${cx(own)} ${own.y + own.h}V${n.y}`,
        future: f ? `M${cx(n)} ${n.y + n.h}V${f.y}` : '',
        feeders: [feeder('next', n), f ? feeder('future', f) : ''].filter(Boolean).join(' '),
      };
      // Sub-pixel differences are rounding, not layout: keeping the old value is what lets a
      // pixel-grid flip settle instead of re-laying out until React gives up.
      setAlign((previous) =>
        (['next', 'future'] as const).every(
          (key) =>
            Math.abs(previous[key].pad - nextAlign[key].pad) < 0.5 &&
            Math.abs(previous[key].box - nextAlign[key].box) < 0.5,
        )
          ? previous
          : nextAlign,
      );
      setWires((previous) =>
        JSON.stringify(previous) === JSON.stringify(measured) ? previous : measured,
      );
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(container);
    container
      .querySelectorAll('[data-journey-node], [data-journey-group], [data-journey-feeders]')
      .forEach((el) => observer?.observe(el));
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [next, future, player.label, opponents, align]);
  if (!next || next.stageType !== 'elimination') return null;
  const compactRound = (fixture: { roundName: string; roundSize?: number }) => {
    const size = fixture.roundSize;
    return size === 2
      ? t('roundFinal')
      : size === 4
        ? t('roundSemi')
        : size === 8
          ? t('roundQuarter')
          : size && size > 8
            ? t('roundOf', { size })
            : fixture.roundName;
  };
  /** The stored match ID (A10) names a feeder; stage, round and number only when it has none. */
  const matchLabel = (source: JourneySource, full: boolean) => {
    const code = displayMatchCode(source.drawMatchId);
    if (code) return full ? [source.stageName, source.roundName, code].filter(Boolean).join(' · ') : code;
    return [
      source.stageName,
      full ? source.roundName : compactRound(source),
      source.matchNumber != null
        ? t(full ? 'matchNumber' : 'matchNumberShort', { number: source.matchNumber })
        : null,
    ]
      .filter(Boolean)
      .join(full ? ' · ' : ' ');
  };
  const sourceLabel = (source: JourneySource, full = false) =>
    t(source.outcome === 'winner' ? 'winnerOf' : 'loserOf', { match: matchLabel(source, full) });
  /** "Winners meet" only when both groups feed the match as winners and its winner comes here. */
  const winnersMeet = (path: JourneyOpponentPath) =>
    path.groups.length === 2 &&
    path.source?.outcome === 'winner' &&
    path.groups.every((group) => {
      const only = group.leaves.length === 1 ? group.leaves[0] : null;
      const edge = group.source ?? (only && only.kind !== 'pending' ? only.source : null);
      return edge?.outcome === 'winner';
    });
  const schedule = (
    fixture: Pick<JourneyFixture, 'scheduledAt' | 'court'> & { courtName?: string | null },
  ) => (
    <>
      {fixture.scheduledAt && (
        <LocalDateTime iso={fixture.scheduledAt} locale={context.locale} format={compactTime} />
      )}
      {fixture.scheduledAt && fixture.court && ' · '}
      {fixture.courtName ?? fixture.court}
    </>
  );
  const caption = (source: JourneySource, settled = false, meet = false) => {
    const label = (full = false) =>
      meet ? t('winnersMeet', { match: matchLabel(source, full) }) : sourceLabel(source, full);
    return (
      <p className="text-muted mt-1 text-[0.6875rem] leading-snug">
        <span title={label(true)} aria-label={label(true)}>
          {label()}
        </span>
        {!settled && (source.scheduledAt || source.court) && (
          <span className="block">{schedule(source)}</span>
        )}
      </p>
    );
  };
  const rung = (fixture: JourneyFixture, prefix: 'next' | 'future') => {
    const groups = fixture.opponentPath.groups;
    const meet = fixture.opponentPath.source && winnersMeet(fixture.opponentPath);
    return (
      <div
        data-journey-stage={prefix}
        className="col-span-3 grid min-w-0 grid-cols-subgrid items-start"
      >
        <div
          data-journey-feeders={prefix}
          className="col-start-1 min-w-0 space-y-4"
          style={{ paddingTop: `${align[prefix].pad}px` }}
        >
          {groups.length ? (
            groups.map((group, gi) => [
              gi === 1 && meet && fixture.opponentPath.source && (
                <div key="meet" data-journey-meet>
                  {caption(fixture.opponentPath.source, false, true)}
                </div>
              ),
              <div key={gi} data-journey-group={`${prefix}-${gi}`} className="min-w-0">
                <div className="space-y-1.5">
                  {group.leaves.map((leaf, li) => {
                    const p =
                      leaf.kind === 'player'
                        ? opponents.find((p) => p.playerId === leaf.playerId)
                        : null;
                    return (
                      <div key={li}>
                        <div
                          data-journey-leaf
                          data-journey-node={`${prefix}-opponent-${gi * 2 + li}`}
                          className="border-border bg-card relative flex min-h-9 min-w-0 flex-wrap items-center justify-between gap-x-1.5 rounded-md border px-2 py-1.5 text-xs pointer-coarse:min-h-11"
                        >
                          {p ? (
                            <>
                              <Link
                                className={`text-foreground min-w-0 flex-1 font-semibold underline-offset-4 after:absolute after:inset-0 hover:underline ${NAME_WRAP}`}
                                href={`/${context.locale}${eventPlayerHref(context.eventId, context.slug, p.playerId, division)}`}
                              >
                                <NameText name={p.label} />
                              </Link>
                              {p.rating != null && (
                                <Numeric
                                  className="bg-surface rounded px-1 py-0.5 text-[0.6875rem]"
                                  aria-label={t('rating', { rating: p.rating })}
                                >
                                  {p.rating}
                                </Numeric>
                              )}
                            </>
                          ) : (
                            <span
                              className="text-muted min-w-0"
                              title={
                                leaf.kind === 'source' ? sourceLabel(leaf.source, true) : undefined
                              }
                              aria-label={
                                leaf.kind === 'source' ? sourceLabel(leaf.source, true) : undefined
                              }
                            >
                              {leaf.kind === 'source'
                                ? sourceLabel(leaf.source)
                                : t('opponentPending')}
                            </span>
                          )}
                        </div>
                        {leaf.kind === 'player' && leaf.source && caption(leaf.source, true)}
                      </div>
                    );
                  })}
                </div>
                {group.source && caption(group.source)}
              </div>,
            ])
          ) : (
            <div data-journey-group={`${prefix}-0`}>
              <div data-journey-leaf className="text-muted bg-card min-w-0 text-xs">
                {t('opponentPending')}
              </div>
            </div>
          )}
          {fixture.opponentPath.source && !meet && caption(fixture.opponentPath.source)}
        </div>
        <div
          data-journey-node={prefix}
          style={{ marginTop: `${align[prefix].box}px` }}
          className={`bg-card relative col-start-3 min-w-[5.625rem] rounded-lg border px-1.5 py-2 text-center text-[0.6875rem] leading-snug ${prefix === 'next' ? 'border-primary bg-primary/10' : 'border-border border-dashed'}`}
        >
          <h3
            className="font-semibold"
            aria-label={[t(prefix === 'next' ? (journey.run.length ? 'nextMatch' : 'firstMatch') : 'ifWin'), displayMatchCode(fixture.drawMatchId), fixture.stageName, fixture.roundName].filter(Boolean).join(' · ')}
          >
            {[t(prefix === 'next' ? 'nextShort' : 'ifWinShort'), displayMatchCode(fixture.drawMatchId), compactRound(fixture)].filter(Boolean).join(' · ')}
          </h3>
          <p className="text-muted mt-0.5">{fixture.stageName}</p>
          <div className="text-muted mt-1">
            {fixture.scheduledAt ? (
              <LocalDateTime
                iso={fixture.scheduledAt}
                locale={context.locale}
                format={compactTime}
              />
            ) : (
              <span title={t('unscheduled')}>{t('unscheduledShort')}</span>
            )}
            {fixture.court && <span className="block">{fixture.courtName ?? fixture.court}</span>}
          </div>
        </div>
      </div>
    );
  };
  return (
    <section
      aria-label={t('path')}
      className="bg-card border-border min-w-0 rounded-xl border p-4 break-words md:p-6"
    >
      <h2 className="font-heading font-semibold">{t('miniDrawTitle')}</h2>
      {/* The round boxes share the right column: it is as wide as the boxes need, up to 45% of the
          bracket, and never narrower than the entrant's longest word (its automatic minimum, kept
          under 50vw so enlarged text cannot push the bracket wider than the screen). A long name
          wraps at its spaces inside the box; the feeder names get the rest. */}
      <div
        ref={root}
        className="relative isolate mx-auto mt-4 grid w-full max-w-[30rem] grid-cols-[minmax(0,1fr)_clamp(1.75rem,12%,2.5rem)_fit-content(45%)] gap-y-7"
      >
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 h-full w-full overflow-visible"
        >
          <path
            data-path="next"
            className="text-primary border-solid"
            d={wires.next}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            data-path="ifWin"
            className="text-muted border-dashed"
            d={wires.future}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeDasharray="1 6"
            strokeLinecap="round"
          />
          <path
            data-path="feeders"
            className="text-muted"
            d={wires.feeders}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        </svg>
        <div
          data-journey-node="own"
          className={`bg-card border-primary relative col-start-3 max-w-[50vw] rounded-md border px-2 py-2 text-center text-xs font-semibold ${NAME_WRAP}`}
        >
          <NameText name={player.label} />
        </div>
        {rung(next, 'next')}
        {future && rung(future, 'future')}
      </div>
      <div className="text-muted mt-4 flex flex-wrap gap-4 text-xs">
        <span className="inline-flex items-center gap-2">
          <span className="border-primary w-5 border-t-2" aria-hidden />
          {t('nextMatch')}
        </span>
        {future && (
          <span className="inline-flex items-center gap-2">
            <span className="border-border w-5 border-t-2 border-dotted" aria-hidden />
            {t('ifWin')}
          </span>
        )}
      </div>
    </section>
  );
}
